import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '../components/ui/accordion';
import { ArrowLeft, HelpCircle, Search, Mail } from 'lucide-react';

const FAQS = [
  {
    q: 'Come faccio a vedere i miei contratti?',
    a: 'Dopo il login, dalla tua area riservata clicca sul tab "Contratti". Troverai l\'elenco dei contratti attivi con date di inizio/fine, canone mensile, immobile collegato e tutti i documenti allegati.',
    cat: 'contratti',
  },
  {
    q: 'Dove trovo le ricevute dei pagamenti?',
    a: 'Apri un contratto e vai alla sezione "Pagamenti". Per ogni canone troverai lo stato (pagato / in attesa / scaduto) e, se disponibile, un pulsante per scaricare la ricevuta in PDF.',
    cat: 'pagamenti',
  },
  {
    q: 'Come scarico i documenti del contratto?',
    a: 'Vai al tab "Documenti" oppure apri un singolo contratto e clicca l\'icona di download accanto al documento. I file sono firmati e archiviati su storage cloud sicuro (Aruba, Italia).',
    cat: 'documenti',
  },
  {
    q: 'Posso cambiare la mia password?',
    a: 'Sì. Dalla tua area riservata, clicca sull\'icona del profilo (in alto a destra) → "Cambia password". Riceverai un\'email con un link per impostare la nuova password.',
    cat: 'account',
  },
  {
    q: 'Cosa devo fare se non ricevo le email da Semplificapp?',
    a: 'Controlla la cartella SPAM/Posta indesiderata. Se non trovi nulla, aggiungi "info@semplificaffitto.it" tra i mittenti fidati e contatta il supporto a info@semplificaffitto.it: aggiorneremo manualmente la tua email.',
    cat: 'account',
  },
  {
    q: 'Come funziona l\'autenticazione a 2 fattori (2FA)?',
    a: 'Per la massima sicurezza, puoi attivare la 2FA dal tuo profilo. Ti verrà chiesto di scansionare un QR code con un\'app authenticator (Google Authenticator, Authy, 1Password). Al login dovrai inserire un codice a 6 cifre generato dall\'app. La 2FA è OBBLIGATORIA per gli amministratori, FACOLTATIVA per gli utenti.',
    cat: 'sicurezza',
  },
  {
    q: 'Posso esportare i miei dati personali (diritto alla portabilità)?',
    a: 'Sì, in conformità al GDPR (Art. 20). Vai al tab "Privacy" della tua area riservata → clicca "Esporta i miei dati": riceverai un file JSON con tutti i tuoi dati personali, contratti, documenti e pagamenti.',
    cat: 'privacy',
  },
  {
    q: 'Come richiedo la cancellazione del mio account?',
    a: 'Dalla tua area riservata, tab "Privacy" → "Richiedi cancellazione account". La tua richiesta verrà valutata dall\'amministratore entro 30 giorni (Art. 17 GDPR). Nota: per obblighi fiscali, i documenti contabili dovranno essere conservati per 10 anni in forma anonimizzata (Art. 2220 c.c.).',
    cat: 'privacy',
  },
  {
    q: 'Posso modificare i miei dati anagrafici (indirizzo, telefono, IBAN)?',
    a: 'Per modificare dati anagrafici sensibili come codice fiscale, residenza, IBAN o documento d\'identità, contatta l\'amministratore via email: la modifica verrà eseguita previo controllo documentale per garantire la tua sicurezza.',
    cat: 'account',
  },
  {
    q: 'Chi contatto in caso di problemi tecnici?',
    a: 'Scrivi a info@semplificaffitto.it specificando: la tua email di login, una descrizione del problema, eventualmente uno screenshot. Riceverai risposta entro 1-2 giorni lavorativi.',
    cat: 'supporto',
  },
];

const CATEGORIES = {
  all: 'Tutte',
  contratti: 'Contratti',
  pagamenti: 'Pagamenti',
  documenti: 'Documenti',
  account: 'Account',
  sicurezza: 'Sicurezza',
  privacy: 'Privacy',
  supporto: 'Supporto',
};

const FaqPage = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [activeCat, setActiveCat] = useState('all');

  const term = search.trim().toLowerCase();
  const filtered = FAQS.filter((f) => {
    if (activeCat !== 'all' && f.cat !== activeCat) return false;
    if (!term) return true;
    return (f.q + ' ' + f.a).toLowerCase().includes(term);
  });

  return (
    <div className="min-h-screen bg-slate-50" data-testid="faq-page">
      <div className="max-w-4xl mx-auto px-4 py-8 sm:py-12">
        <button
          onClick={() => navigate(-1)}
          className="text-sm text-slate-600 hover:text-slate-900 flex items-center gap-1 mb-4"
          data-testid="faq-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Torna indietro
        </button>

        <div className="flex items-start gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center shrink-0">
            <HelpCircle className="w-7 h-7" strokeWidth={1.8} />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Domande frequenti</h1>
            <p className="text-slate-600 mt-1">
              Le risposte alle 10 domande che ci vengono poste più spesso.
            </p>
          </div>
        </div>

        {/* Search + categorie */}
        <Card className="p-4 mb-6">
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              type="text"
              placeholder="Cerca tra le domande..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="faq-search-input"
            />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(CATEGORIES).map(([key, label]) => (
              <button
                key={key}
                onClick={() => setActiveCat(key)}
                className={`px-3 py-1 text-xs rounded-full font-medium transition-colors ${
                  activeCat === key
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
                data-testid={`faq-cat-${key}`}
              >
                {label}
              </button>
            ))}
          </div>
        </Card>

        {/* Lista FAQ */}
        <Card className="overflow-hidden">
          {filtered.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              Nessuna domanda trovata per "{search}".
            </div>
          ) : (
            <Accordion type="single" collapsible className="divide-y">
              {filtered.map((f, idx) => (
                <AccordionItem
                  key={`${f.cat}-${idx}`}
                  value={`item-${idx}`}
                  className="px-4 border-0"
                  data-testid={`faq-item-${idx}`}
                >
                  <AccordionTrigger className="text-left hover:no-underline py-4 text-base font-medium text-slate-900">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 leading-relaxed pb-4">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </Card>

        {/* Contatto supporto */}
        <Card className="mt-6 p-6 bg-slate-900 text-white">
          <div className="flex items-start gap-4">
            <Mail className="w-6 h-6 text-blue-300 shrink-0 mt-1" />
            <div className="flex-1">
              <h3 className="font-semibold text-lg">Non hai trovato la risposta?</h3>
              <p className="text-slate-300 text-sm mt-1 mb-3">
                Scrivici a info@semplificaffitto.it e ti rispondiamo entro 1-2 giorni lavorativi.
              </p>
              <a
                href="mailto:info@semplificaffitto.it"
                className="inline-block"
                data-testid="faq-mailto-link"
              >
                <Button variant="secondary" size="sm">
                  Contatta il supporto
                </Button>
              </a>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default FaqPage;
